import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Loader2, Sparkles, CheckCircle, CreditCard, Lock, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { updateUser } from '../store/actions/authActions';
import toast from 'react-hot-toast';
import paymentService from '../services/paymentService';

// Custom Logo matching the gradient sparkles design
const DOCSPRINT_LOGO = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwIiB5MT0iMCIgeDI9IjEiIHkyPSIxIj4KICAgICAgPHN0b3Agb2Zmc2V0PSIwIiBzdG9wLWNvbG9yPSIjZmI5MjNjIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iI2VhNTgwYyIvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIHJ4PSI0MCIgZmlsbD0idXJsKCNnKSIvPgogIDwhLS0gQ2VudGVyIFN0YXIgLS0+CiAgPHBhdGggZD0iTTEwMCA0MCBMMTE1IDg1IEwxNjAgMTAwIEwxMTUgMTE1IEwxMDAgMTYwIEw4NSAxMTUgTDQwIDEwMCBMODUgODUgWiIgZmlsbD0id2hpdGUiLz4KICA8IS0tIFNtYWxsIFN0YXIgLS0+CiAgPHBhdGggZD0iTTE2MCA0MCBMMTY1IDU1IEwxODAgNjAgTDE2NSA2NSBMMTYwIDgwIEwxNTUgNjUgTDE0MCA2MCBMMTU1IDU1IFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPg==";

const PLANS = {
  starter: {
    name: 'Starter',
    price: 20,
    tokens: '1M',
    tokensRaw: 1_000_000,
    dailyTokens: '100K',
    projects: 3,
    features: [
      '1,000,000 AI tokens',
      '100K tokens / day',
      '3 projects',
      'All core AI features',
      'Priority support',
    ],
    gradient: 'from-orange-500 to-amber-500',
    shadowColor: 'shadow-orange-500/30',
    planType: 'starter' as const,
  },
  intelligence: {
    name: 'Intelligence',
    price: 49,
    tokens: '5M',
    tokensRaw: 5_000_000,
    dailyTokens: '500K',
    projects: Infinity,
    features: [
      '5,000,000 AI tokens',
      '500K tokens / day',
      'Unlimited projects',
      'All AI features + Market Intelligence',
      'Top-up available ($30 → +5M tokens)',
      'Priority 24/7 support',
    ],
    gradient: 'from-purple-600 to-indigo-600',
    shadowColor: 'shadow-purple-500/30',
    planType: 'intelligence' as const,
  },
};

type PlanKey = keyof typeof PLANS;

export const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

  const planKey: PlanKey = (searchParams.get('plan') as PlanKey) || 'intelligence';
  const plan = PLANS[planKey] || PLANS.intelligence;
  const [amountInPaise, setAmountInPaise] = useState(Math.round(plan.price * 83 * 100));

  useEffect(() => {
    if (!isAuthenticated && !localStorage.getItem('token')) {
      toast.error('Session expired. Please login to continue.');
      navigate('/login');
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setIsRazorpayLoaded(true);
    script.onerror = () => toast.error('Razorpay SDK failed to load. Please check your connection.');
    document.body.appendChild(script);

    fetch('https://api.exchangerate-api.com/v4/latest/USD')
      .then(res => res.json())
      .then(data => {
        const rate = data.rates.INR;
        setAmountInPaise(Math.round(plan.price * rate * 100));
      })
      .catch(() => {});

    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, [isAuthenticated, navigate, plan.price]);

  const handlePayment = async () => {
    if (!isRazorpayLoaded) {
      toast.error('Payment gateway is still initializing. Please wait.');
      return;
    }
    setIsLoading(true);

    try {
      // Create Razorpay Order on Backend
      const orderRes = await paymentService.createOrder(plan.planType);
      if (!orderRes.success) {
        toast.error(orderRes.message || 'Failed to initiate payment.');
        setIsLoading(false);
        return;
      }

      if (orderRes.bypass) {
        const result = await paymentService.upgradePlan(plan.planType);
        if (result?.success) {
          dispatch(updateUser({
            ...user,
            subscribed: 'S',
            planType: plan.planType,
            tokensTotal: plan.tokensRaw,
            tokensRemaining: plan.tokensRaw,
            projectsAllowed: plan.projects === Infinity ? 999 : plan.projects,
          } as any));
          toast.success(`🎉 Welcome to ${plan.name} Plan! ${plan.tokens} tokens ready.`);
          setTimeout(() => navigate('/dashboard'), 1500);
        } else {
          toast.error(result.message || 'Transaction failed');
        }
        setIsLoading(false);
        return;
      }

      const options = {
        key: orderRes.key_id || '',
        amount: orderRes.amount,
        currency: orderRes.currency || 'INR',
        name: 'DocSprintAI',
        description: `${plan.name} Plan — ${plan.tokens} tokens`,
        image: DOCSPRINT_LOGO,
        order_id: orderRes.order_id,
        handler: async function (rzpResponse: any) {
          try {
            const result = await paymentService.verifyPayment(
              rzpResponse.razorpay_payment_id,
              rzpResponse.razorpay_order_id,
              rzpResponse.razorpay_signature
            );
            if (result?.success) {
              dispatch(updateUser({
                ...user,
                subscribed: 'S',
                planType: plan.planType,
                tokensTotal: plan.tokensRaw,
                tokensRemaining: plan.tokensRaw,
                projectsAllowed: plan.projects === Infinity ? 999 : plan.projects,
              } as any));
              toast.success(`🎉 Welcome to ${plan.name} Plan! ${plan.tokens} tokens ready.`);
              setTimeout(() => navigate('/dashboard'), 1500);
            } else {
              toast.error(result.message || 'Failed to provision resources');
            }
          } catch (error: any) {
            const msg = error.response?.data?.message || error.message;
            toast.error(`Payment successful but upgrade failed: ${msg}. Contact support.`);
          } finally {
            setIsLoading(false);
          }
        },
        prefill: {
          name: (user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : user?.username || 'User',
          email: user?.email || '',
          contact: user?.contactNumber || '',
        },
        theme: { color: planKey === 'intelligence' ? '#7c3aed' : '#f97316' },
        modal: {
          ondismiss: () => setIsLoading(false),
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
      rzp.on('payment.failed', (resp: any) => {
        toast.error(resp.error.description || 'Payment failed');
        setIsLoading(false);
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white font-sans flex flex-col relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[120px] -z-10 animate-pulse-slow"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-rose-500/10 rounded-full blur-[100px] -z-10"></div>

      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 text-sm font-medium group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back
          </button>

          {/* Plan Selector */}
          <div className="flex gap-3 mb-6">
            {(['starter', 'intelligence'] as PlanKey[]).map((p) => (
              <button
                key={p}
                onClick={() => navigate(`/payment?plan=${p}`)}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-bold transition-all border ${planKey === p
                  ? `bg-gradient-to-r ${PLANS[p].gradient} border-transparent text-white shadow-lg`
                  : 'border-white/10 text-gray-400 hover:text-white hover:border-white/20'}`}
              >
                {PLANS[p].name}
              </button>
            ))}
          </div>

          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
            <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${plan.gradient}`}></div>

            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">{plan.name} Plan</h1>
                <p className="text-gray-400 text-sm">{plan.tokens} tokens · one-time</p>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center text-white shadow-lg`}>
                {planKey === 'intelligence' ? <Zap size={22} /> : <Sparkles size={22} />}
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-2 mb-8">
              <span className="text-5xl font-black text-white">${plan.price}</span>
              <span className="text-gray-400 text-sm">one-time · {plan.tokens} tokens</span>
            </div>

            {/* Features */}
            <div className="bg-white/5 rounded-2xl p-5 mb-8 border border-white/5">
              <ul className="space-y-3">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-gray-300">
                    <CheckCircle size={16} className="text-emerald-500 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 text-xs text-gray-500 bg-white/5 p-3 rounded-lg border border-white/5">
                <ShieldCheck size={16} className="text-emerald-500" />
                <span>Payments are secure and encrypted via Razorpay</span>
              </div>

              <Button
                onClick={handlePayment}
                disabled={isLoading || !isRazorpayLoaded}
                className={`w-full h-14 rounded-xl bg-gradient-to-r ${plan.gradient} text-white font-bold hover:brightness-110 shadow-lg ${plan.shadowColor} transition-all flex items-center justify-center gap-2 border-none`}
              >
                {isLoading ? (
                  <><Loader2 size={20} className="animate-spin" /> Processing...</>
                ) : (
                  <>Pay ${plan.price} <Lock size={16} /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
